import {
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type ReactNode,
	type SetStateAction,
	useContext,
	useLayoutEffect,
	useState,
} from "react";

export type PageHeaderState = {
	title: string;
	subtitle?: string;
	/**
	 * Right-aligned action slot (typically a CTA button or a button cluster).
	 * Pages stash interactive header content here so the layout shell can render
	 * it next to the title without each page rebuilding its own header bar.
	 */
	actions?: ReactNode;
};

const PageHeaderStateContext = createContext<PageHeaderState | null>(null);
const PageHeaderSetterContext = createContext<Dispatch<
	SetStateAction<PageHeaderState>
> | null>(null);

export const PageHeaderProvider = ({ children }: PropsWithChildren) => {
	const [header, setHeader] = useState<PageHeaderState>({ title: "" });
	return (
		<PageHeaderStateContext.Provider value={header}>
			<PageHeaderSetterContext.Provider value={setHeader}>
				{children}
			</PageHeaderSetterContext.Provider>
		</PageHeaderStateContext.Provider>
	);
};

export const usePageHeaderState = (): PageHeaderState => {
	const value = useContext(PageHeaderStateContext);
	if (value === null) {
		throw new Error(
			"usePageHeaderState must be used inside <PageHeaderProvider>",
		);
	}
	return value;
};

// Pages call this near the top of the component to publish their header
// (title + optional subtitle + optional actions) into the AuthenticatedLayout shell.
export const usePageHeader = ({
	title,
	subtitle,
	actions,
}: PageHeaderState): void => {
	const setHeader = useContext(PageHeaderSetterContext);
	if (setHeader === null) {
		throw new Error("usePageHeader must be used inside <PageHeaderProvider>");
	}
	useLayoutEffect(() => {
		setHeader({ title, subtitle, actions });
	}, [setHeader, title, subtitle, actions]);
};
